import React, { useState, useEffect } from 'react';
import {
  ClockIcon,
  UserIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
  KeyIcon
} from '@heroicons/react/24/outline';
import { kioskAPI, kioskSettingsAPI } from '../services/api';

const Kiosk = () => {
  const [currentStep, setCurrentStep] = useState('kioskLogin'); // kioskLogin, selectEmployee, enterPin, success
  const [kioskAuthenticated, setKioskAuthenticated] = useState(false);
  const [kioskLoginData, setKioskLoginData] = useState({
    username: 'kiosk',
    password: ''
  });
  const [kioskSettings, setKioskSettings] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Auto-reset timer
  const [autoResetTimer, setAutoResetTimer] = useState(null);

  useEffect(() => {
    checkKioskAuth();
  }, []);

  const checkKioskAuth = async () => {
    // Check if kiosk is configured for auto-login
    try {
      const storedAuth = localStorage.getItem('kioskAuth');
      if (storedAuth) {
        const authData = JSON.parse(storedAuth);
        if (authData.auto_login_enabled) {
          setKioskAuthenticated(true);
          setKioskSettings(authData);
          setCurrentStep('selectEmployee');
          loadEmployees();
          return;
        }
      }
      
      // If no auto-login, show login screen
      setCurrentStep('kioskLogin');
    } catch (error) {
      console.error('Error checking kiosk auth:', error);
      setCurrentStep('kioskLogin');
    }
  };

  // Auto-reset after 30 seconds of inactivity
  useEffect(() => {
    const resetTimer = () => {
      if (autoResetTimer) {
        clearTimeout(autoResetTimer);
      }
      
      const timer = setTimeout(() => {
        resetKiosk();
      }, 30000); // 30 seconds
      
      setAutoResetTimer(timer);
    };

    if (currentStep !== 'selectEmployee') {
      resetTimer();
    }

    return () => {
      if (autoResetTimer) {
        clearTimeout(autoResetTimer);
      }
    };
  }, [currentStep, selectedEmployee, pin]);

  const loadEmployees = async () => {
    try {
      const response = await kioskAPI.getEmployees();
      setEmployees(response.data.employees);
    } catch (error) {
      console.error('Error loading employees:', error);
      setMessage('Failed to load employees');
    }
  };

  const handleKioskLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await kioskSettingsAPI.authenticateKiosk(
        kioskLoginData.username, 
        kioskLoginData.password
      );
      
      const authData = {
        authenticated: true,
        auto_login_enabled: response.data.settings.auto_login_enabled,
        session_timeout_minutes: response.data.settings.session_timeout_minutes,
        timestamp: Date.now()
      };
      
      // Store auth data for auto-login if enabled
      if (response.data.settings.auto_login_enabled) {
        localStorage.setItem('kioskAuth', JSON.stringify(authData));
      }
      
      setKioskAuthenticated(true);
      setKioskSettings(authData);
      setCurrentStep('selectEmployee');
      loadEmployees();
    } catch (error) {
      console.error('Kiosk login error:', error);
      setMessage(error.response?.data?.message || 'Invalid kiosk credentials');
    } finally {
      setLoading(false);
    }
  };

  const resetKiosk = () => {
    if (kioskAuthenticated) {
      setCurrentStep('selectEmployee');
    } else {
      setCurrentStep('kioskLogin');
    }
    setSelectedEmployee(null);
    setPin('');
    setMessage('');
    setAttendanceStatus(null);
    setSearchTerm('');
    if (autoResetTimer) {
      clearTimeout(autoResetTimer);
      setAutoResetTimer(null);
    }
  };

  const selectEmployee = async (employee) => {
    if (!employee.has_pin) {
      setMessage('This employee has not set up a kiosk PIN. Please contact your administrator.');
      return;
    }

    setSelectedEmployee(employee);
    setCurrentStep('enterPin');
    setMessage('');
    
    // Load attendance status
    try {
      const response = await kioskAPI.getAttendanceStatus(employee.id);
      setAttendanceStatus(response.data);
    } catch (error) {
      console.error('Error loading attendance status:', error);
    }
  };

  const handlePinSubmit = async () => {
    if (!pin || pin.length < 4) {
      setMessage('Please enter a valid PIN');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      // Determine if this is check-in or check-out
      const isCheckIn = !attendanceStatus?.isCheckedIn;
      
      if (isCheckIn) {
        const response = await kioskAPI.checkIn(selectedEmployee.id, pin);
        setMessage(response.data.message);
        setCurrentStep('success');
        
        // Auto-reset after 3 seconds
        setTimeout(() => {
          resetKiosk();
        }, 3000);
      } else {
        const response = await kioskAPI.checkOut(selectedEmployee.id, pin);
        setMessage(response.data.message);
        setCurrentStep('success');
        
        // Auto-reset after 3 seconds
        setTimeout(() => {
          resetKiosk();
        }, 3000);
      }
    } catch (error) {
      console.error('Error with attendance action:', error);
      setMessage(error.response?.data?.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePinInput = (digit) => {
    if (pin.length < 8) {
      setPin(pin + digit);
    }
  };

  const handlePinBackspace = () => {
    setPin(pin.slice(0, -1));
  };

  const filteredEmployees = employees.filter(employee =>
    employee.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.employee_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderKioskLogin = () => (
    <div className="max-w-md mx-auto">
      <div className="text-center mb-8">
        <div className="bg-primary-100 rounded-full p-6 w-24 h-24 mx-auto mb-6">
          <KeyIcon className="h-12 w-12 text-primary-600" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Kiosk Mode</h1>
        <p className="text-lg text-gray-600">Enter kiosk credentials to continue</p>
      </div>

      <form onSubmit={handleKioskLogin} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Username
          </label>
          <input
            type="text"
            className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            value={kioskLoginData.username}
            onChange={(e) => setKioskLoginData(prev => ({ ...prev, username: e.target.value }))}
            required
            disabled={loading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Password
          </label>
          <input
            type="password"
            className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            value={kioskLoginData.password}
            onChange={(e) => setKioskLoginData(prev => ({ ...prev, password: e.target.value }))}
            required
            disabled={loading}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg text-lg font-medium hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
        >
          {loading ? 'Authenticating...' : 'Access Kiosk'}
        </button>
      </form>

      {message && (
        <div className="mt-6 p-4 rounded-lg bg-red-50 border border-red-200">
          <p className="text-sm text-red-800 text-center">{message}</p>
        </div>
      )}
    </div>
  );

  const renderSelectEmployee = () => (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <ClockIcon className="h-16 w-16 text-primary-600 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900">Employee Check-In/Out</h1>
        <p className="text-lg text-gray-600 mt-2">Select your name to continue</p>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search by name or employee number..."
          className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Employee Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-h-96 overflow-y-auto">
        {filteredEmployees.map((employee) => (
          <button
            key={employee.id}
            onClick={() => selectEmployee(employee)}
            disabled={!employee.has_pin}
            className={`p-4 rounded-lg border-2 transition-all ${
              employee.has_pin
                ? 'border-gray-200 hover:border-primary-500 hover:bg-primary-50 bg-white'
                : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
            }`}
          >
            <UserIcon className="h-12 w-12 mx-auto mb-2 text-gray-600" />
            <div className="text-center">
              <p className="font-medium text-gray-900">
                {employee.preferred_name || employee.first_name} {employee.last_name}
              </p>
              <p className="text-sm text-gray-500">{employee.employee_number}</p>
              <p className="text-xs text-gray-500 mt-1">{employee.department}</p>
              {!employee.has_pin && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                    <XCircleIcon className="h-3 w-3 mr-1" />
                    No PIN
                  </span>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>

      {filteredEmployees.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">No employees found matching your search.</p>
        </div>
      )}
    </div>
  );

  const renderEnterPin = () => (
    <div className="max-w-md mx-auto">
      <div className="text-center mb-8">
        <div className="bg-primary-100 rounded-full p-4 w-20 h-20 mx-auto mb-4">
          <ShieldCheckIcon className="h-12 w-12 text-primary-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">
          Hello, {selectedEmployee?.preferred_name || selectedEmployee?.first_name}
        </h1>
        <p className="text-gray-600 mt-2">Enter your PIN to continue</p>
        
        {/* Current Status */}
        {attendanceStatus && (
          <div className="mt-4 p-3 rounded-lg bg-blue-50 border border-blue-200">
            <p className="text-sm text-blue-800">
              {attendanceStatus.isCheckedIn ? (
                <>
                  <CheckCircleIcon className="h-4 w-4 inline mr-1" />
                  You are currently checked in. Click continue to check out.
                </>
              ) : (
                <>
                  <ClockIcon className="h-4 w-4 inline mr-1" />
                  Ready to check in for today.
                </>
              )}
            </p>
          </div>
        )}
      </div>

      {/* PIN Display */}
      <div className="mb-6">
        <div className="flex justify-center space-x-2 mb-4">
          {[...Array(8)].map((_, index) => (
            <div
              key={index}
              className={`w-3 h-3 rounded-full border-2 ${
                index < pin.length
                  ? 'bg-primary-600 border-primary-600'
                  : 'bg-white border-gray-300'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Number Pad */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
          <button
            key={digit}
            onClick={() => handlePinInput(digit.toString())}
            disabled={loading}
            className="w-16 h-16 text-xl font-semibold bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {digit}
          </button>
        ))}
        <button
          onClick={handlePinBackspace}
          disabled={loading || pin.length === 0}
          className="w-16 h-16 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
        >
          ←
        </button>
        <button
          onClick={() => handlePinInput('0')}
          disabled={loading}
          className="w-16 h-16 text-xl font-semibold bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
        >
          0
        </button>
        <button
          onClick={handlePinSubmit}
          disabled={loading || pin.length < 4}
          className="w-16 h-16 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
        >
          {loading ? '...' : '✓'}
        </button>
      </div>

      {/* Back Button */}
      <div className="flex justify-center space-x-4">
        <button
          onClick={resetKiosk}
          disabled={loading}
          className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back
        </button>
      </div>

      {message && (
        <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200">
          <p className="text-sm text-red-800 text-center">{message}</p>
        </div>
      )}
    </div>
  );

  const renderSuccess = () => (
    <div className="max-w-md mx-auto text-center">
      <div className="bg-green-100 rounded-full p-6 w-24 h-24 mx-auto mb-6">
        <CheckCircleIcon className="h-12 w-12 text-green-600" />
      </div>
      
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Success!</h1>
      <p className="text-lg text-gray-600 mb-4">{message}</p>
      
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <p className="text-sm text-gray-600">
          Returning to main screen in 3 seconds...
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-100 py-8 px-4">
      <div className="container mx-auto">
        {currentStep === 'kioskLogin' && renderKioskLogin()}
        {currentStep === 'selectEmployee' && renderSelectEmployee()}
        {currentStep === 'enterPin' && renderEnterPin()}
        {currentStep === 'success' && renderSuccess()}
      </div>
    </div>
  );
};

export default Kiosk;